<template>
  <div :class="isValidPassAndCenteredQR ? 'centered-qr-footer' : 'footer'">
    <!-- カウントダウン表示(一部チケットのみ) -->
    <FaceCountDown v-if="isCountdownType" :count-down="props.countDown" />

    <!-- QRコードが取得前に表示されるのを防ぐ -->
    <div v-if="isLoadingQRInfo" />

    <FaceSliderQRWithBackgroundImage
      v-else-if="props.isQrSliderPass"
      :is-centered-q-r="props.isValidPassAndCenteredQR"
      :is-valid-qr-string="isValidQrForQrTicketGate"
      :qr-string="qrStringForQrTicketGate"
      :on-tap-reload="onTapReloadCenteredQR"
      :has-network-speed-issue="hasNetworkSpeedIssue"
      :is-loading-q-r-info="isLoadingQRInfo"
      :background-image-url="props.qrcodeBackgroundImageUrl"
    />

    <!-- 現在時刻+拡大QR -->
    <FaceCurrentTimeAndExpandedCenteredQR
      v-else-if="props.isValidPassAndCenteredQR"
      :animation-url="animationUrl"
      :is-valid-qr-string="isValidQrForQrTicketGate"
      :qr-string="qrStringForQrTicketGate"
      :on-tap-reload="onTapReloadCenteredQR"
      :has-network-speed-issue="hasNetworkSpeedIssue"
      :is-loading-q-r-info="isLoadingQRInfo"
    />

    <!-- QR改札(QR+＋アニメーション) -->
    <FaceAnimationAndQR
      v-else-if="isQrTicketGate"
      :animation-url="animationUrl"
      :is-valid-qr-string="isValidQrForQrTicketGate"
      :qr-string="qrStringForQrTicketGate"
      :on-tap-reload="onTapReload"
    />

    <!-- QR改札以外(QRのみ or アニメーションのみ) -->
    <div v-else>
      <!-- QR(サンリオ) -->
      <PFaceQRLink v-if="isQrPass" :qr-string="qrString" />

      <PFaceOldQRLink v-else-if="isOldQrPass" :qr-link="qrLink" />

      <!-- アニメーション -->
      <FaceAnimation v-else :url="animationUrl" />
    </div>
  </div>

  <!-- ローディングダイアログ -->
  <DialogLoading ref="DialogLoading" />
</template>

<script>
import { onMounted, computed, ref, watchEffect } from "vue";
import { useStore } from "vuex";

import API from "@/API";

import passService from "@/services/pass";
import nativeHandlerService from "@/services/nativeHandler";

import { encryptAES256CBC } from "@/utils/Encrypt.js";
import FaceSliderQRWithBackgroundImage from "@/views/Components/Molecules/MyTicket/FaceSliderQRWithBackgroundImage.vue";

export default {
  name: "ThePassLowerAfterValid",
  components: { FaceSliderQRWithBackgroundImage },
  props: {
    countDown: {
      type: String,
    },
    isValidPassAndCenteredQR: {
      type: Boolean,
      required: true,
    },
    updatePassForQrTicketGate: {
      type: Function,
      required: true,
    },
    isFooter: {
      type: Boolean,
      default: true,
    },
    isOfflineQr: {
      type: Boolean,
      default: false,
    },
    isQrSliderPass: {
      type: Boolean,
      default: false,
    },
    qrcodeBackgroundImageUrl: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const store = useStore();

    const book = computed(() => store.getters["ticketing/book"]);
    const pass = computed(() => store.getters["ticketing/pass"]);
    const masterPass = computed(() => store.getters["ticketing/masterPass"]);

    const DialogLoading = ref(null);
    const hasNetworkSpeedIssue = ref(false);

    const isCountdownType = computed(() =>
      passService.checkIsCountdownPass(masterPass.value)
    );

    // QRチケット用
    const isQrPass = passService.checkIsQrPass(masterPass.value);
    const qrString = ref(null);

    // 旧QRチケット用(2022.4までのサンリオ 終売後削除予定)
    const isOldQrPass = passService.checkIsOldQrPass(masterPass.value);
    const qrLink = ref(null);
    // QR改札用
    const isQrTicketGate = computed(() =>
      passService.checkIsQrTicketGate(pass.value)
    );
    const qrInfoForQrTicketGate = ref(null);
    const isValidQrForQrTicketGate = ref(null);
    const interval = ref(null);
    const qrDisplayStartTime = ref(null);
    const retryTimeout = ref(null);
    const isLoadingQRInfo = ref(false);
    const offlineQrRes = ref(null);
    // QR改札用のQRコード文字列を共通化
    const qrStringForQrTicketGate = computed(() => {
      return qrInfoForQrTicketGate.value == null
        ? ""
        : qrInfoForQrTicketGate.value.qrcode_string;
    });

    // アニメーションURLを共通化
    const animationUrl = computed(() => {
      return pass.value.pass_animation_image_url || String();
    });

    /**
     * オフラインQRコードを取得して暗号化し、QR情報オブジェクトを生成
     * @param {number} bookGroupId - ブックグループID
     * @param {number} masterPassId - マスターパスID
     * @returns {Object} QR情報オブジェクト { qrcode_string, limited_at }
     */
    const createOfflineQrInfo = async (bookGroupId, masterPassId) => {
      offlineQrRes.value = await API.getOfflineQrCode(
        bookGroupId,
        masterPassId
      );

      if (!offlineQrRes.value) {
        throw new Error("オフラインQRコードのレスポンスが空です");
      }

      const jsonString = JSON.stringify(offlineQrRes.value);
      // rPはQRリフレッシュ間隔
      const limitedAt = new Date(Date.now() + offlineQrRes.value.rP * 1000);

      if (!jsonString || jsonString === "null" || jsonString === "{}") {
        throw new Error("暗号化するデータが無効です");
      }

      const encryptedRes = encryptAES256CBC(jsonString);

      return {
        qrcode_string: encryptedRes,
        limited_at: limitedAt,
      };
    };

    const getQrInfo = async (timeout = null) => {
      // リロード時など、intervalが動いていればクリア
      if (interval.value != null) {
        clearInterval(interval.value);
      }

      isLoadingQRInfo.value = true;
      try {
        if (props.isOfflineQr) {
          qrInfoForQrTicketGate.value = await createOfflineQrInfo(
            book.value.book_group_id,
            masterPass.value.id
          );
        } else {
          qrInfoForQrTicketGate.value = await API.getQrInfoForQrTicketGate(
            pass.value.id,
            timeout
          );
        }
      } catch (error) {
        console.error(`Error in getQrInfo: ${error}`);
        throw error;
      } finally {
        isLoadingQRInfo.value = false;
      }

      // QRが有効かどうかをチェック(10秒周期)
      updateIsValidQrCode();
      interval.value = setInterval(() => updateIsValidQrCode(), 10 * 1000);
    };

    const updateIsValidQrCode = () => {
      isValidQrForQrTicketGate.value = passService.checkIsValidQrCode(
        qrInfoForQrTicketGate.value.limited_at
      );
    };

    // QRコードのステータスに応じて、画面の輝度を調整(有効な場合は明るく、無効な場合は戻す)
    const changeBrightnessOfScreen = (isValidQr) => {
      if (isValidQr) {
        nativeHandlerService.ncSetBrightnessMax();
      } else {
        nativeHandlerService.ncRevertBrightness();
      }
    };

    const onTapReloadCenteredQR = async () => {
      hasNetworkSpeedIssue.value = false;

      const shouldResetRetryTimeout = retryTimeout.value;
      if (shouldResetRetryTimeout) {
        clearTimeout(retryTimeout.value);
        retryTimeout.value = null;
      }

      try {
        // リロード時はAPIコール中にloading表示
        DialogLoading.value.openDialog();
        // pass情報取得
        await props.updatePassForQrTicketGate(3000);
        // passが有効なら、QR情報取得（3秒タイムアウト）
        if (!pass.value.is_expired) {
          await getQrInfo(3000);
        }
      } catch (error) {
        if (props.isOfflineQr && qrInfoForQrTicketGate.value) {
          const EXTENSION_TIME_MS = 3 * 60 * 1000;
          qrInfoForQrTicketGate.value.limited_at = new Date(
            Date.now() + EXTENSION_TIME_MS
          );
          // QRが有効かどうかを再チェック
          updateIsValidQrCode();
        }

        // ネットワークエラー時(hasNetworkSpeedIssue)のフラグを立て、黄色背景を表示
        hasNetworkSpeedIssue.value = true;
        const NETWORK_ERROR_TIMEOUT_MIN = 3;
        const NETWORK_ERROR_TIMEOUT_MS = NETWORK_ERROR_TIMEOUT_MIN * 60 * 1000;
        // 指定した時間後にhasNetworkSpeedIssueをfalseに変更する
        retryTimeout.value = setTimeout(() => {
          hasNetworkSpeedIssue.value = false;
        }, NETWORK_ERROR_TIMEOUT_MS);
      } finally {
        if (DialogLoading.value) {
          DialogLoading.value.closeDialog();
        }
      }
    };

    const onTapReload = async () => {
      // リロード時はAPIコール中にloading表示
      DialogLoading.value.openDialog();
      // pass情報取得
      await props.updatePassForQrTicketGate();
      // passが有効なら、QR情報取得
      if (!pass.value.is_expired) {
        await getQrInfo();
      }
      if (DialogLoading.value) {
        DialogLoading.value.closeDialog();
      }
    };

    onMounted(async () => {
      try {
        // QRチケット(サンリオ)の場合
        if (isQrPass) {
          qrString.value = await API.getQrString(pass.value.id);
          nativeHandlerService.ncSetBrightnessMax();
        }
        // 旧QRチケット(サンリオ 2022.4まで)の場合
        else if (isOldQrPass) {
          qrLink.value = await API.getQrLink(pass.value.id);
          nativeHandlerService.ncSetBrightnessMax();
        } else if (isQrTicketGate.value || props.isQrSliderPass) {
          await getQrInfo();
        }
      } catch (e) {
        console.log(`Error in ThePassLowerAfterValid onMounted() :${e}`);
      }
    });

    watchEffect(() => {
      // QRステータスが変わった際は、画面輝度を変更
      if (isValidQrForQrTicketGate.value != null) {
        changeBrightnessOfScreen(isValidQrForQrTicketGate.value);
      }
    });

    return {
      qrInfoForQrTicketGate,
      qrStringForQrTicketGate,
      qrLink,
      qrString,
      pass,
      masterPass,
      isCountdownType,
      isQrPass,
      isOldQrPass,
      isQrTicketGate,
      isValidQrForQrTicketGate,
      animationUrl,
      onTapReload,
      onTapReloadCenteredQR,
      props,
      DialogLoading,
      hasNetworkSpeedIssue,
      isLoadingQRInfo,
    };
  },
};
</script>

<style scoped>
.centered-qr-footer {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>
